import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Table, Card, Button, Space, Tag, Typography, Popconfirm, message } from 'antd';
import { StopOutlined, CheckOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import OptOutModal, { RecordConsentModal } from './OptOutModal.component';
import SingleEntitySelect from '../crud/SingleEntitySelect.component';
import PatientUploadedFiles from '../patient/PatientUploadedFiles.component';
import {
    getVaultPatientSubStudies,
    getVaultPatientUploadBatches,
    getVaultPatientDetails,
    listVaultSubStudies,
    enrolVaultPatientsInSubStudy,
    unenrolVaultPatientFromSubStudy,
} from '../../actions/identifierVaultActions';
import { showApiError, formatApiError } from '../../utils/formatApiError';

const { Text } = Typography;

/** Consent, site fields, vault uploaded files, and sub-study enrol via SingleEntitySelect. */
export default function ParticipantRelations({ participantId }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { subStudies = [], loading: loadingSubStudies } = useSelector((s) => s.vaultPatientSubStudies || {});
    const { vaultPatient } = useSelector((s) => s.vaultPatientDetails || {});

    const [optOutOpen, setOptOutOpen] = useState(false);
    const [consentOpen, setConsentOpen] = useState(false);
    const [studyToEnrol, setStudyToEnrol] = useState(null);
    const [enrolling, setEnrolling] = useState(false);

    useEffect(() => {
        if (!participantId) return;
        dispatch(getVaultPatientSubStudies(participantId));
    }, [dispatch, participantId]);

    const isWithdrawn = vaultPatient?.isOptedOut === true;

    const refreshAfterOptOut = () => {
        dispatch(getVaultPatientDetails(participantId));
    };

    const refreshSubStudies = () => dispatch(getVaultPatientSubStudies(participantId));

    const handleEnrol = async () => {
        if (!studyToEnrol) return;
        setEnrolling(true);
        try {
            await dispatch(enrolVaultPatientsInSubStudy(studyToEnrol, [participantId]));
            message.success(t('Enrolled in sub-study'));
            setStudyToEnrol(null);
            refreshSubStudies();
        } catch (err) {
            showApiError(formatApiError(err, t), t);
        } finally {
            setEnrolling(false);
        }
    };

    const handleUnenrol = async (subStudyId) => {
        try {
            await dispatch(unenrolVaultPatientFromSubStudy(subStudyId, participantId));
            message.success(t('Removed from sub-study'));
            refreshSubStudies();
        } catch (err) {
            showApiError(formatApiError(err, t), t);
        }
    };

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 24 }}>
            <Card
                size="small"
                title={t('Consent')}
                extra={
                    isWithdrawn ? (
                        <Tag color="red">{t('Withdrawn')}{vaultPatient?.optOutSource ? ` — ${vaultPatient.optOutSource}` : ''}</Tag>
                    ) : (
                        <Space>
                            <Tag color={vaultPatient?.isOptedIn ? 'green' : 'default'}>
                                {vaultPatient?.isOptedIn ? t('Opted in') : t('No recorded consent')}
                            </Tag>
                            {!vaultPatient?.isOptedIn && (
                                <Button size="small" icon={<CheckOutlined />} onClick={() => setConsentOpen(true)}>
                                    {t('Record consent')}
                                </Button>
                            )}
                            <Button danger size="small" icon={<StopOutlined />} onClick={() => setOptOutOpen(true)}>
                                {t('Record withdrawal')}
                            </Button>
                        </Space>
                    )
                }
            >
                <Space direction="vertical" size={4}>
                    <Text type="secondary">
                        {t('Study Hash')}:{' '}
                        {vaultPatient?.studyHash ? (
                            <Text code copyable>
                                {vaultPatient.studyHash}
                            </Text>
                        ) : (
                            t('not yet computed')
                        )}
                    </Text>
                    {isWithdrawn && (
                        <Text type="secondary">
                            {t('Identifiers were erased on withdrawal; the Study Hash is retained so this participant cannot be re-added (protocol §5.3).')}
                        </Text>
                    )}
                </Space>
            </Card>

            <Card size="small" title={t('Site enrolment')}>
                <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={vaultPatient?.id ? [vaultPatient] : []}
                    columns={[
                        {
                            title: t('Organisation'),
                            dataIndex: 'organisationId',
                            render: (v) => (v ? <Text copyable={{ text: String(v) }}>{v}</Text> : '—'),
                        },
                        {
                            title: t('Site-Study ID'),
                            dataIndex: 'siteStudyId',
                            render: (v) => (v ? <Text copyable>{v}</Text> : <Text type="secondary">—</Text>),
                        },
                        {
                            title: t('External PROM ID'),
                            dataIndex: 'externalPROMId',
                            render: (v) => (v ? <Text copyable>{v}</Text> : <Text type="secondary">—</Text>),
                        },
                        {
                            title: t('Patient Identifier'),
                            dataIndex: 'patientIdentifier',
                            render: (v) => (v ? <Text copyable>{v}</Text> : <Text type="secondary">—</Text>),
                        },
                    ]}
                />
            </Card>

            {participantId && (
                <PatientUploadedFiles
                    patientId={participantId}
                    patientIdentifier={vaultPatient?.patientIdentifier}
                    loadBatches={getVaultPatientUploadBatches}
                    batchesSelectorKey="vaultPatientUploadBatches"
                    readOnly
                />
            )}

            <Card
                size="small"
                title={`${t('Sub-studies')} (${subStudies.length})`}
                extra={
                    <Space.Compact style={{ maxWidth: 420 }}>
                        <div style={{ minWidth: 260 }}>
                            <SingleEntitySelect
                                singularEntity="vaultSubStudy"
                                pluralEntity="vaultSubStudies"
                                constantPrefix="VAULT_SUB_STUDY"
                                listEntities={listVaultSubStudies}
                                primarySearchProp="futureStudyCode"
                                secondarySearchProp="description"
                                enableCreate={false}
                                value={studyToEnrol}
                                onChange={(id) => setStudyToEnrol(id || null)}
                                placeholder={t('Add to sub-study')}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            disabled={!studyToEnrol}
                            loading={enrolling}
                            onClick={handleEnrol}
                        >
                            {t('Enrol')}
                        </Button>
                    </Space.Compact>
                }
            >
                <Table
                    rowKey="id"
                    size="small"
                    loading={loadingSubStudies}
                    dataSource={subStudies}
                    pagination={false}
                    columns={[
                        { title: t('Sub-study'), dataIndex: 'futureStudyCode' },
                        {
                            title: t('Created'),
                            dataIndex: 'createdAt',
                            render: (v) => (v ? new Date(v).toLocaleDateString() : '—'),
                        },
                        {
                            title: '',
                            key: 'actions',
                            width: 48,
                            render: (_, row) => (
                                <Popconfirm
                                    title={t('Remove from this sub-study?')}
                                    onConfirm={() => handleUnenrol(row.id || row.Id)}
                                    okText={t('Remove')}
                                    cancelText={t('Cancel')}
                                >
                                    <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                                </Popconfirm>
                            ),
                        },
                    ]}
                />
            </Card>

            <OptOutModal
                open={optOutOpen}
                onClose={() => setOptOutOpen(false)}
                participantId={participantId}
                participantLabel={vaultPatient?.studyHash}
                onCompleted={refreshAfterOptOut}
            />
            <RecordConsentModal
                open={consentOpen}
                onClose={() => setConsentOpen(false)}
                participantId={participantId}
                participantLabel={vaultPatient?.studyHash}
                onCompleted={refreshAfterOptOut}
            />
        </Space>
    );
}
